function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that manages the popover + popover trigger relationship for anchoring.
 * Calculates the trigger position and inlines custom properties on the popover element
 * that can be consumed by CSS for positioning.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} popover – The popover element.
 * @property {HTMLElement} trigger – The popover trigger element.
 *
 * @extends Component<Refs>
 *
 * @example
 * ```html
 * <anchored-popover-component data-close-on-resize>
 *   <button data-ref="trigger" popovertarget="menu">Open Menu</button>
 *   <div data-ref="popover" id="menu" popover>Menu content</div>
 * </anchored-popover-component>
 * ```
 *
 * @property {string[]} requiredRefs - Required refs: 'popover' and 'trigger'
 * @property {number} [interaction_delay] - The delay in milliseconds for the hover interaction
 * @property {string} [data-close-on-resize] - When present, closes popover on window resize
 * @property {string} [data-hover-triggered] - When present, makes the popover function via pointerenter/leave
 * @property {number | null} [popoverTrigger] - The timeout for the popover trigger
 */
var _popoverTrigger = _classPrivateFieldLooseKey("popoverTrigger");
var _onTriggerEnter = _classPrivateFieldLooseKey("onTriggerEnter");
var _onTriggerLeave = _classPrivateFieldLooseKey("onTriggerLeave");
var _onPopoverEnter = _classPrivateFieldLooseKey("onPopoverEnter");
var _onPopoverLeave = _classPrivateFieldLooseKey("onPopoverLeave");
var _updatePosition = _classPrivateFieldLooseKey("updatePosition");
var _resizeListener = _classPrivateFieldLooseKey("resizeListener");
export class AnchoredPopoverComponent extends Component {
  constructor(...args) {
    super(...args);
    this.requiredRefs = ['popover', 'trigger'];
    this.interaction_delay = 200;
    Object.defineProperty(this, _popoverTrigger, {
      writable: true,
      value: (/** @type {number | null} */null)
    });
    Object.defineProperty(this, _onTriggerEnter, {
      writable: true,
      value: () => {
        const {
          trigger,
          popover
        } = this.refs;
        trigger.dataset.hoverActive = 'true';
        if (!popover.matches(':popover-open')) {
          _classPrivateFieldLooseBase(this, _popoverTrigger)[_popoverTrigger] = setTimeout(() => {
            if (trigger.matches('[data-hover-active]')) popover.showPopover();
          }, this.interaction_delay);
        }
      }
    });
    Object.defineProperty(this, _onTriggerLeave, {
      writable: true,
      value: () => {
        const {
          trigger,
          popover
        } = this.refs;
        delete trigger.dataset.hoverActive;
        if (_classPrivateFieldLooseBase(this, _popoverTrigger)[_popoverTrigger]) clearTimeout(_classPrivateFieldLooseBase(this, _popoverTrigger)[_popoverTrigger]);
        if (popover.matches(':popover-open')) {
          _classPrivateFieldLooseBase(this, _popoverTrigger)[_popoverTrigger] = setTimeout(() => {
            popover.hidePopover();
          }, this.interaction_delay);
        }
      }
    });
    Object.defineProperty(this, _onPopoverEnter, {
      writable: true,
      value: () => {
        if (_classPrivateFieldLooseBase(this, _popoverTrigger)[_popoverTrigger]) clearTimeout(_classPrivateFieldLooseBase(this, _popoverTrigger)[_popoverTrigger]);
      }
    });
    Object.defineProperty(this, _onPopoverLeave, {
      writable: true,
      value: () => {
        const {
          popover
        } = this.refs;
        _classPrivateFieldLooseBase(this, _popoverTrigger)[_popoverTrigger] = setTimeout(() => {
          popover.hidePopover();
        }, this.interaction_delay);
      }
    });
    /**
     * Updates the popover position by calculating trigger element bounds
     * and setting CSS custom properties on the popover element.
     */
    Object.defineProperty(this, _updatePosition, {
      writable: true,
      value: async () => {
        const {
          popover,
          trigger
        } = this.refs;
        if (!popover || !trigger) return;
        const positions = trigger.getBoundingClientRect();
        popover.style.setProperty('--anchor-top', `${positions.top}`);
        popover.style.setProperty('--anchor-right', `${window.innerWidth - positions.right}`);
        popover.style.setProperty('--anchor-bottom', `${window.innerHeight - positions.bottom}`);
        popover.style.setProperty('--anchor-left', `${positions.left}`);
        popover.style.setProperty('--anchor-height', `${positions.height}`);
        popover.style.setProperty('--anchor-width', `${positions.width}`);
      }
    });
    /**
     * Debounced resize handler that optionally closes the popover
     * when the window is resized, based on the data-close-on-resize attribute.
     */
    Object.defineProperty(this, _resizeListener, {
      writable: true,
      value: debounce(() => {
        const popover = /** @type {HTMLElement} */this.refs.popover;
        if (popover && popover.matches(':popover-open')) {
          popover.hidePopover();
        }
      }, 100)
    });
  }
  /**
   * Component initialization - sets up event listeners for resize and popover toggle events.
   */
  connectedCallback() {
    super.connectedCallback();
    const {
      popover,
      trigger
    } = this.refs;
    if (this.dataset.closeOnResize) {
      popover.addEventListener('beforetoggle', event => {
        const evt = /** @type {ToggleEvent} */event;
        window[evt.newState === 'open' ? 'addEventListener' : 'removeEventListener']('resize', _classPrivateFieldLooseBase(this, _resizeListener)[_resizeListener]);
      });
    }
    if (this.dataset.hoverTriggered) {
      trigger.addEventListener('pointerenter', _classPrivateFieldLooseBase(this, _onTriggerEnter)[_onTriggerEnter]);
      trigger.addEventListener('pointerleave', _classPrivateFieldLooseBase(this, _onTriggerLeave)[_onTriggerLeave]);
      popover.addEventListener('pointerenter', _classPrivateFieldLooseBase(this, _onPopoverEnter)[_onPopoverEnter]);
      popover.addEventListener('pointerleave', _classPrivateFieldLooseBase(this, _onPopoverLeave)[_onPopoverLeave]);
    }
    if (!CSS.supports('position-anchor: --trigger')) {
      popover.addEventListener('beforetoggle', () => {
        _classPrivateFieldLooseBase(this, _updatePosition)[_updatePosition]();
      });
      requestIdleCallback(() => {
        _classPrivateFieldLooseBase(this, _updatePosition)[_updatePosition]();
      });
    }
  }

  /**
   * Component cleanup - removes resize event listener.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', _classPrivateFieldLooseBase(this, _resizeListener)[_resizeListener]);
  }
}
if (!customElements.get('anchored-popover-component')) {
  customElements.define('anchored-popover-component', AnchoredPopoverComponent);
}
